import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';

import {
  getBookToken as book_token_cookies,
  setBookToken,
} from '@/components/shared/getToken';

import { Endpoints } from '@/network';
import { getAllChaptersApi, getBookToken } from '@/services/Library/Books';
import { ChapterVideoPlayer } from '@/utils/interfaces/Library/Pdf';

import VideoPlayer from './VideoPlayer';

interface PlayerWithChaptersProps {
  /**
   * When rendered inside PdfReader's modal, pass these directly.
   * When rendered on the standalone /pdf/[book_id]/[chapter_id] page,
   * omit them — the component falls back to router.query (no breaking change).
   */
  bookId?: string;
  chapterId?: string;
  /** When provided, the back button closes the modal instead of navigating. */
  onBack?: () => void;
}

const PlayerWithChapters = ({
  bookId: propBookId,
  chapterId: propChapterId,
  onBack,
}: PlayerWithChaptersProps = {}) => {
  const [chaptersList, setChaptersList] = useState<ChapterVideoPlayer[]>([]);
  const baseUrl = process.env.NEXT_PUBLIC_API_READER_URL;
  const router = useRouter();

  // In modal mode we cannot change the URL, so track the active chapter locally.
  const [localChapterId, setLocalChapterId] = useState<string | undefined>(
    propChapterId,
  );

  // Modal mode when props are supplied; standalone page mode otherwise.
  const isModalMode = Boolean(propBookId);
  const book_id = propBookId ?? (router.query.book_id as string | undefined);
  const chapter_id = isModalMode
    ? localChapterId
    : (router.query.chapter_id as string | undefined);

  const handleGetBookToken = async () => {
    try {
      const response = await getBookToken();
      await setBookToken(response?.data?.file_token);
    } catch (error) {
      console.error('API call error:', error);
    }
  };

  const getAllChaptersListData = async (id: string) => {
    try {
      const response = await getAllChaptersApi(id);
      let token = await book_token_cookies();

      if (token === undefined || token === 'undefined') {
        await handleGetBookToken();
        token = await book_token_cookies();
      }

      const updatedChapters = response?.chapters
        ?.filter((chapter: any) => Boolean(chapter.videoUrl))
        .map((chapter: any) => ({
          ...chapter,
          videoUrl: `${baseUrl}/${Endpoints.getChapterVideoStream}/${chapter._id}/video?token=${token}`,
        }));

      setChaptersList(updatedChapters || []);
    } catch (error) {
      console.error('API call error:', error);
    }
  };

  useEffect(() => {
    if (book_id) getAllChaptersListData(book_id);
  }, [book_id]);

  const current = useMemo(() => {
    if (!chaptersList || chaptersList.length === 0) return undefined;

    if (chapter_id) {
      const found = chaptersList.find((ch) => ch._id === chapter_id);
      if (found) return found;
    }

    return chaptersList[0];
  }, [chapter_id, chaptersList]);

  const handleChapterChange = (chapter: ChapterVideoPlayer) => {
    if (isModalMode) {
      // Modal mode — update local state only; URL stays unchanged.
      setLocalChapterId(chapter._id);
    } else {
      // Standalone page mode — keep the existing shallow-routing behaviour.
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, chapter_id: chapter._id },
        },
        undefined,
        { shallow: true },
      );
    }
  };

  return (
    <div className='w-full h-full relative'>
      {current && (
        <VideoPlayer
          poster={current.videoThumbnailUrl}
          src={current.videoUrl as string}
          currentChapterTitle={current.name}
          bookName={current.book?.title}
          chaptersList={chaptersList}
          setCurrent={handleChapterChange}
          chapterNumber={current.chapterNumber}
          currentChapterDuration={current.videoMetaData?.duration}
          onBack={onBack}
        />
      )}
    </div>
  );
};

export default PlayerWithChapters;
